/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import _ from "lodash";

import { INavigationLinkProps } from "Components/NavigationLink/INavigationLinkProps";
import { BenefitManagerNavigationLink } from "Interfaces/Benefits/IBenefitManagerNavigationLink";
import AccessControlIcon from "../Assets/Icons/AccessControl.svg";
import ApplicationIcon from "../Assets/Icons/Application.svg";
import BenefitsIcon from "../Assets/Icons/Benefits.svg";
import DistributionListIcon from "../Assets/Icons/DL.svg";
import EventIcon from "../Assets/Icons/Event.svg";
import MLSAIcon from "../Assets/Icons/MLSA.svg";
import MVPIcon from "../Assets/Icons/MVP.svg";
import PendingRequestIcon from "../Assets/Icons/PendingRequest.svg";
import RDIcon from "../Assets/Icons/RD.svg";
import SearchIcon from "../Assets/Icons/Search.svg";
import { ReviewSubSiteUrlPath } from "./ApplicationConstants";
import { BenefitsSubSiteUrlPath, BenefitDisplayNameEnum } from "./BenefitConstants";
import { RolesEnum } from "./RolesPermissionsConstants";
import { TenantTypesEnum } from "./TenantTypes";

// TODO: Fix all MSA/MSP/MLSA consistency issues

/**
 * Enum describing different pages on site
 */
export enum SideNavLinksEnum {
  HEADER = "header",
  USERS = "users",
  EVENTS = "events",
  APPLICATIONS = "applications",
  REVIEW = "review",
  ACCESS = "access",
  BENEFITS = "benefits",
  DL = "distributionLists",
  AWARDCATEGORIES = "awardCategories",
}

// BenefitManager respose interface
interface BenefitManagerResponse {
  hasError: boolean;
  loading: boolean;
  data: BenefitManagerNavigationLink[];
}

/**
 * A function helper to build out the applications link
 * per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const accessControlLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    icon: AccessControlIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.ACCESS}`,
    tenant: tenant,
    text: "Access Control",
  };
};

/**
 * A function helper to build out the applications link
 * per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const applicationsLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    icon: ApplicationIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.APPLICATIONS}`,
    tenant: tenant,
    text: "Applications",
  };
};

/**
 * A function helper to build out the review link
 * per tenant. Review replaces Applications parent level link.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const reviewLink = (tenant: TenantTypesEnum, role: RolesEnum): INavigationLinkProps => {
  const subLinks: INavigationLinkProps[] = [];

  const applications: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.REVIEW}/${
      ReviewSubSiteUrlPath.applications
    }`,
    tenant: tenant,
    text: "Applications",
  };

  const demographics: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.REVIEW}/${
      ReviewSubSiteUrlPath.demographics
    }`,
    tenant: tenant,
    text: "Demographics",
  };

  const subNavigationByRoles = {
    [RolesEnum.MVP_PROGRAM_OWNER]: [applications, demographics],
    [RolesEnum.MVP_REVIEWER]: [applications, demographics],
    [RolesEnum.MVP_TECH_AREA_VALIDATOR]: [applications],
    [RolesEnum.MVP_BUSINESS_GROUP]: [applications, demographics],
    [RolesEnum.MVP_EXECUTIVE]: [applications],
    [RolesEnum.MVP_BENEFITS_MANAGER]: [applications, demographics],
  };

  subNavigationByRoles[role]?.forEach((subLink) => {
    subLinks.push(subLink);
  });

  return {
    icon: ApplicationIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.APPLICATIONS}`,
    tenant: tenant,
    text: "Review",
    subLinks,
  };
};

/**
 * A function helper to build out the events link
 * per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const eventsLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    icon: EventIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.EVENTS}`,
    tenant: tenant,
    text: "Events",
  };
};

/**
 * A function helper to build out the header link
 * per tenant.
 * @param {string} icon
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const headerLink = (icon: string, tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    header: false,
    icon: icon,
    source: `/${tenant.toLowerCase()}`,
    tenant: tenant,
    text: tenant === "MLSA" ? "Students" : tenant,
  };
};

/**
 * A function helper to build out the applications link
 * per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const benefitsLink = (
  tenant: TenantTypesEnum,
  benefitManagerResponse: BenefitManagerResponse
): INavigationLinkProps => {
  const subLinks: INavigationLinkProps[] = [];

  const manageCodes: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.manageCodes
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.ManageCodes,
  };

  const azureRequests: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.azureRequests
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.AzureRequests,
  };

  const gitHubProBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.gitHubPro
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.GitHubPro,
  };

  const linkedInLearningBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.linkedInLearning
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.LinkedInLearning,
  };

  const M365Benefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${BenefitsSubSiteUrlPath.O365}`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.O365,
  };

  const m365CopilotBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.m365Copilot
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.M365Copilot,
  };

  const mcpExamVoucherBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${BenefitsSubSiteUrlPath.mcp}`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.MCP,
  };

  const visualStudioBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.visualStudio
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.VisualStudio,
  };

  const swagKitBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.swagKit
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.SwagKit,
  };

  const teamsBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${BenefitsSubSiteUrlPath.teams}`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.Teams,
  };

  const awardKitBenefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${
      BenefitsSubSiteUrlPath.awardKit
    }`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.AwardKit,
  };

  const microsoft365Benefit: INavigationLinkProps = {
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${BenefitsSubSiteUrlPath.MS365}`,
    tenant: tenant,
    text: BenefitDisplayNameEnum.MS365,
  };

  switch (tenant) {
    case TenantTypesEnum.MLSA:
      subLinks.push(manageCodes);
      subLinks.push(visualStudioBenefit);
      subLinks.push(swagKitBenefit);
      break;
    case TenantTypesEnum.MVP:
      subLinks.push(manageCodes);
      subLinks.push(azureRequests);
      subLinks.push(gitHubProBenefit);
      subLinks.push(linkedInLearningBenefit);
      subLinks.push(M365Benefit);
      subLinks.push(microsoft365Benefit);
      subLinks.push(m365CopilotBenefit);
      subLinks.push(mcpExamVoucherBenefit);
      subLinks.push(visualStudioBenefit);
      subLinks.push(teamsBenefit);
      subLinks.push(awardKitBenefit);
      break;
    case TenantTypesEnum.RD:
      subLinks.push(azureRequests);
      subLinks.push(gitHubProBenefit);
      subLinks.push(linkedInLearningBenefit);
      subLinks.push(M365Benefit);
      subLinks.push(microsoft365Benefit);
      subLinks.push(m365CopilotBenefit);
      subLinks.push(visualStudioBenefit);
      subLinks.push(teamsBenefit);
      break;
    default:
      break;
  }

  // push Yaml based benefit's links for MVP
  if (
    tenant === TenantTypesEnum.MVP &&
    !benefitManagerResponse.hasError &&
    !benefitManagerResponse.loading &&
    benefitManagerResponse.data &&
    benefitManagerResponse.data.length > 0
  ) {
    subLinks.push(
      ...benefitManagerResponse.data.map((ben) => ({
        source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}/${ben.id}`,
        tenant: tenant,
        text: ben.navTitle,
      }))
    );
  }

  return {
    icon: BenefitsIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.BENEFITS}`,
    tenant: tenant,
    text: "Benefits",
    subLinks,
  };
};

/**
 * A function helper to build out the show all users
 * (i.e., - students, mvps, or rds) link per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const usersLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    icon: SearchIcon,
    source: `/${tenant.toLowerCase()}/${tenant.toLowerCase()}s`,
    tenant: tenant,
    text: tenant === "MLSA" ? "Students" : `${tenant}s`,
  };
};

/**
 * A function helper to build out the show all users
 * (i.e., - students, mvps, or rds) link per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const awardCategoriesLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  return {
    icon: DistributionListIcon,
    source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.AWARDCATEGORIES}`,
    tenant: tenant,
    text: "Tech Structure",
  };
};

/**
 * A function helper to build out the show all dls
 * (i.e., - students, mvps, or rds) link per tenant.
 * @param {string} tenant
 * @returns {INavigationLinkProps}
 */
const distributionListsLink = (tenant: TenantTypesEnum): INavigationLinkProps => {
  const mvpSubLinks: INavigationLinkProps[] = [
    {
      icon: PendingRequestIcon,
      source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.DL}/pending`,
      tenant: tenant,
      text: "Pending Request",
    },
    {
      icon: DistributionListIcon,
      source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.DL}`,
      tenant: tenant,
      text: "Distribution Lists",
    },
  ];

  const rdSubLinks: INavigationLinkProps[] = [
    {
      icon: DistributionListIcon,
      source: `/${tenant.toLowerCase()}/${SideNavLinksEnum.DL}`,
      tenant: tenant,
      text: "Distribution Lists",
    },
  ];

  const subLinks = tenant === TenantTypesEnum.MVP ? mvpSubLinks : rdSubLinks;

  return {
    icon: DistributionListIcon,
    source: `/${tenant}/distributionLists`,
    tenant: tenant,
    text: "DLs",
    subLinks,
  };
};

export const sideNavigationByRoles = {
  [RolesEnum.MVP_PROGRAM_OWNER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.REVIEW,
    SideNavLinksEnum.ACCESS,
    SideNavLinksEnum.AWARDCATEGORIES,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.MVP_REVIEWER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.REVIEW,
    SideNavLinksEnum.ACCESS,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.MVP_TECH_AREA_VALIDATOR]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.REVIEW,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.MVP_BENEFITS_MANAGER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.REVIEW,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.MVP_BUSINESS_GROUP]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.REVIEW,
    SideNavLinksEnum.ACCESS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.MVP_EXECUTIVE]: [SideNavLinksEnum.HEADER, SideNavLinksEnum.REVIEW],
  [RolesEnum.MSA_PROGRAM_OWNER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.EVENTS,
    SideNavLinksEnum.APPLICATIONS,
    SideNavLinksEnum.ACCESS,
    SideNavLinksEnum.BENEFITS,
  ],
  [RolesEnum.MSA_REVIEWER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.EVENTS,
    SideNavLinksEnum.APPLICATIONS,
    SideNavLinksEnum.BENEFITS,
  ],
  [RolesEnum.MSA_BENEFITS_MANAGER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.BENEFITS,
  ],
  [RolesEnum.MSA_GUEST]: [SideNavLinksEnum.HEADER],
  [RolesEnum.RD_PROGRAM_OWNER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.APPLICATIONS,
    SideNavLinksEnum.ACCESS,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.RD_REVIEWER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.APPLICATIONS,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.RD_BENEFITS_MANAGER]: [
    SideNavLinksEnum.HEADER,
    SideNavLinksEnum.USERS,
    SideNavLinksEnum.BENEFITS,
    SideNavLinksEnum.DL,
  ],
  [RolesEnum.RD_GUEST]: [SideNavLinksEnum.HEADER, SideNavLinksEnum.APPLICATIONS],
};

const getLink = (
  program: string,
  linkType: string,
  role: string,
  benefitManagerResponse: BenefitManagerResponse
): INavigationLinkProps => {
  if (program === "MSA") program = "MLSA";

  switch (linkType) {
    case SideNavLinksEnum.HEADER:
      if (program === TenantTypesEnum.MLSA) return headerLink(MLSAIcon, program);
      if (program === TenantTypesEnum.MVP) return headerLink(MVPIcon, program);
      if (program === TenantTypesEnum.RD) return headerLink(RDIcon, program);
      break;
    case SideNavLinksEnum.USERS:
      return usersLink(program as TenantTypesEnum);
    case SideNavLinksEnum.EVENTS:
      return eventsLink(program as TenantTypesEnum);
    case SideNavLinksEnum.APPLICATIONS:
      return applicationsLink(program as TenantTypesEnum);
    case SideNavLinksEnum.REVIEW:
      return reviewLink(program as TenantTypesEnum, role as RolesEnum);
    case SideNavLinksEnum.ACCESS:
      return accessControlLink(program as TenantTypesEnum);
    case SideNavLinksEnum.AWARDCATEGORIES:
      return awardCategoriesLink(program as TenantTypesEnum);
    case SideNavLinksEnum.BENEFITS:
      return benefitsLink(program as TenantTypesEnum, benefitManagerResponse);
    case SideNavLinksEnum.DL:
      return distributionListsLink(program as TenantTypesEnum);
  }

  return {
    icon: "",
    source: "/",
    tenant: "",
    text: "Home",
  };
};

export const getSideNavigationLinksForUser = (
  roles: string[],
  benefitManagerResponse: BenefitManagerResponse
): INavigationLinkProps[] => {
  const links: INavigationLinkProps[] = [];
  if (!roles) return links;
  roles.sort();
  roles.forEach((role: string) => {
    const roleMatch = role.match(/(\w+)-(.+)/);
    if (roleMatch) {
      const tenant = roleMatch[1].toUpperCase();
      sideNavigationByRoles[role]?.forEach((linkType: string) => {
        const link: INavigationLinkProps = getLink(tenant, linkType, role, benefitManagerResponse);
        const index = links.findIndex((l) => link.source === l.source);
        if (index === -1) {
          links.push(link);
        } else if (!!links[index].subLinks && !!link.subLinks) {
          links[index].subLinks = _.unionBy(links[index].subLinks, link.subLinks, "source");
        }
      });
    }
  });

  return links;
};
